import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { API } from '../utils/api';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * AuthCallback — handles the Supabase OAuth redirect (Google / Apple).
 * 1. Supabase SDK parses the URL and establishes a session (detectSessionInUrl).
 * 2. We send the Supabase access token to /api/auth/supabase-exchange, which links/provisions
 *    the PayMig user and returns the profile.
 * 3. New social users without a Swish alias are routed to complete registration (unchanged UX).
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { setProfile, clear } = useAuthContext();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        if (!supabase) {
          toast.error('Social login is not configured.');
          navigate('/', { replace: true });
          return;
        }
        // Establish the Supabase session from the redirect URL (PKCE code exchange).
        let session = (await supabase.auth.getSession()).data.session;
        if (!session) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (error) throw error;
          session = data.session;
        }
        if (!session?.access_token) {
          toast.error('Sign in was cancelled or failed');
          clear();
          navigate('/', { replace: true });
          return;
        }

        // Link/provision the PayMig user using the Supabase JWT.
        const response = await axios.post(
          `${API}/auth/supabase-exchange`,
          {},
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );

        const user = response.data.user;
        if (!user) throw new Error('Invalid response from server');

        // New social user that still needs Swish/role setup → profile completion.
        const needsProfileCompletion = !user.swish_payee_alias && user.user_type !== 'BUSINESS';
        if (needsProfileCompletion) {
          toast.info('Please complete your registration');
          navigate('/', {
            replace: true,
            state: {
              googleUser: user,
              isGoogleComplete: true,
            },
          });
        } else {
          setProfile(user);
          toast.success('Logged in successfully!');
          navigate('/dashboard', { replace: true, state: { user } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
        if (error.response?.status === 403 && typeof error.response?.data?.detail === 'string') {
          sessionStorage.setItem('auth_error', error.response.data.detail);
        }
        toast.error(errorMessage);
        clear();
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-green-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
